"use client"

import * as React from "react"
import { animate, stagger, utils } from "animejs"

import { whenInView, type RevealTrigger } from "./reveal"
import { useMotionScope } from "../../hooks/use-motion-scope"
import { blur, distance, duration, ease, staggerStep } from "../../lib/motion"

function Stagger({
  trigger = "mount",
  step = staggerStep.base,
  delay = 0,
  offset = distance.sm,
  from = "first",
  replayKey,
  ...props
}: React.ComponentProps<"div"> & {
  trigger?: RevealTrigger
  step?: number
  delay?: number
  offset?: number
  from?: "first" | "center" | "last"
  replayKey?: React.Key
}) {
  const { root } = useMotionScope<HTMLDivElement>(
    ({ root, reduced, scope }) => {
      if (reduced) {
        root.dataset.motionState = "done"
        return
      }
      const play = () =>
        scope.execute(() => {
          const items = Array.from(root.children)
          utils.set(items, { opacity: 0, translateY: offset, filter: `blur(${blur.sm}px)` })
          root.dataset.motionState = "done"
          animate(items, {
            opacity: 1,
            translateY: 0,
            filter: "blur(0px)",
            duration: duration.slow,
            delay: stagger(step, { start: delay, from }),
            ease: ease.enter,
          })
        })
      if (trigger === "mount") {
        play()
        return
      }
      return whenInView(root, play)
    },
    [trigger, step, delay, offset, from, replayKey]
  )

  return <div ref={root} data-slot="stagger" data-motion="stagger" {...props} />
}

export { Stagger }
